import type { MethodologyEntity } from '@protector/entities/methodology/models';
import type { IndicatorDTO, Phenomenon } from '@protector/entities/phenomenon/models';
import { entity } from '@protector/entities/property/__legacy__/redux.reducer';
import type { PropertyState } from '@protector/entities/property/__legacy__/redux.types';
import { reportOnce } from '@protector/api-client';
import type { Property, PropertyInfoAddress } from '@protector/entities/property/models';
import { getSelectedPropertyId, selectSelectedCompany, type Selector } from 'core/core.selectors';
import { hasRTRPlanEntitlement } from 'entities/company/company.selectors';
import { isGrapeCrop } from 'entities/crop/crop.domain';
import type { EntityState } from 'redux-ngrx-entity';
import type { AppState } from 'redux/app-state';
import { createSelector } from 'reselect';

export { getSelectedProperty, selectPropertyEntities } from '@protector/entities/property/__legacy__/redux.selectors';

export const getPropertyPhenomenons = (state: AppState): Phenomenon[] => state.entities.property.propertyPhenomenons;
export const getPropertyIndicators = (state: AppState): IndicatorDTO[] => state.entities.property.propertyIndicators;
export const getPropertyMethodologies = (state: AppState): MethodologyEntity[] => state.entities.property.propertyMethodologies;
export const isPropertyPhenomenonsLoading = (state: AppState): boolean => state.entities.property.isLoadingPropertyPhenomena;
export const isIndicatorPressureLoading: Selector<boolean> = state => state.entities.property.isIndicatorPressureLoading;
export const getPropertyEntities: Selector<EntityState<Property>['entities']> = state => state.entities.property.entities;

export const getPropertyReferencePoint: Selector<PropertyInfoAddress['referencePoint'] | undefined> = state =>
  state.entities.property.propertyData?.referencePoint;

export const selectCurrentProperty = createSelector([getSelectedPropertyId, getPropertyEntities], (currentPropertyId, propertyEntities) =>
  currentPropertyId ? propertyEntities[currentPropertyId] : null
);

export const selectHasGrapeCrop = createSelector(selectCurrentProperty, currentProperty => {
  if (typeof currentProperty === 'string') {
    return false;
  }

  return currentProperty?.seasons?.some(s => isGrapeCrop(s.crop));
});

export const selectsTheFirstPropertyIdOfTheEntities = createSelector(getPropertyEntities, propertyEntities => {
  const propertyIds = Object.keys(propertyEntities);
  return propertyIds?.[0];
});

export const getIsLoadedPropertyAreaInfo: Selector<boolean> = state => state.entities.property.isLoadedPropertyAreaInfo;

export const getPropertySelectedInfoBasic: Selector<PropertyInfoAddress | null> = state => state.entities.property.propertyData;

export const getPropertyTime: Selector<PropertyState['propertyTime']> = state => state.entities.property.propertyTime;

const { selectAll } = entity.getSelectors();

const hasSeasons = (property: Property | undefined): boolean => Boolean(property?.seasons?.length);

export const selectAllProperties = createSelector(
  (state: AppState) => selectAll(state.entities.property),
  hasRTRPlanEntitlement,
  (properties, showPropertiesWithoutSeasons) => {
    if (!properties) return [];

    if (showPropertiesWithoutSeasons) {
      return properties;
    }

    return properties.filter(property => {
      if (property && property.seasons === undefined) {
        reportOnce(
          'property.selectors:selectAllProperties',
          () => new Error('property.selectors: property.seasons missing -- selectAllProperties is dropping properties'),
          { errorType: 'StateFallback', domain: 'Property', propertyId: property.id }
        );
      }
      return hasSeasons(property);
    });
  }
);

export const getSelectedPropertyByCompany = createSelector(
  selectAllProperties,
  (state: AppState) => selectSelectedCompany(state.uiState.global),
  (properties, selectedCompany) => properties.filter(property => property.company_id === selectedCompany)
);

export const getAllPropertiesByCompany = createSelector(
  (state: AppState) => selectAll(state.entities.property),
  (state: AppState) => selectSelectedCompany(state.uiState.global),
  (properties, selectedCompany) => properties.filter(property => property.company_id === selectedCompany)
);

export const filterPropertiesIds = createSelector(getAllPropertiesByCompany, properties => properties.map(property => property.id));

export const getPropertiesIndexedNameByCurrentCompany = createSelector(getAllPropertiesByCompany, properties =>
  properties.map(property => ({ id: property.id, name: property.name }))
);
